import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Clock, Copy, FileImage, Mail, ShieldCheck, Upload } from 'lucide-react';
import copy from 'copy-to-clipboard';
import { callAPI, IdentityRecoveryRetry } from '../hooks/api';
import { UserData } from '../hooks/auth';
import { toApiUrl } from '../utils/apiBase';
import { canonical_recovery_identification, validate_email } from '../utils/validate';
import { Button } from '../shadcn/Button';
import { cn } from '../shadcn/utils';
import { Dialog } from './Dialog';
import Input from './Input';
import Spinner from './Spinner';

const ENDPOINT = 'https://api.krece.app/administration/identity-recovery/';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_PIXELS = 16000000;
const RESPONSE_TIMEOUT_MS = 120000;
const EMAIL_ERROR_CODES = ['invalid_email', 'email_in_use', 'email_reserved', 'same_email'];

export interface IdentityRecoveryClient {
	identification_number: string;
	email: string;
}

type RecoveryStep = 'form' | 'confirm' | 'processing' | 'completed' | 'pending' | 'unknown' | 'debt';
type SavedRecovery = IdentityRecoveryRetry & {
	state: 'unknown' | 'pending' | 'completed';
};
type RecoverySession = SavedRecovery & {
	current_email: string;
	locked: boolean;
};
type Photo = { file: File; url: string };

export function can_recover_identity(user: UserData | null, country: string) {
	return (
		country === 'venezuela' &&
		user?.is_active === true &&
		['atc', 'ata', 'admin', 'postventa', 'manager', 'finanzas', 'admin_finanzas'].includes(user.access_type || '')
	);
}

function read_recoveries(prefix: string): SavedRecovery[] {
	const requests: SavedRecovery[] = [];
	for (let index = 0; index < localStorage.length; index++) {
		const key = localStorage.key(index);
		if (!key?.startsWith(prefix)) continue;
		try {
			const value: unknown = JSON.parse(localStorage.getItem(key) || 'null');
			if (!value || typeof value !== 'object') continue;
			const request = value as Partial<SavedRecovery>;
			if (
				typeof request.request_id === 'string' &&
				key === prefix + request.request_id &&
				/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(request.request_id) &&
				typeof request.email === 'string' &&
				validate_email(request.email) &&
				typeof request.expected_identification_number === 'string' &&
				request.expected_identification_number.length > 0 &&
				['unknown', 'pending', 'completed'].includes(request.state || '')
			) {
				requests.push(request as SavedRecovery);
			}
		} catch {
			// Ignore malformed entries; never delete or reuse an unrecognized request ID.
		}
	}
	return requests;
}

function RecoveryNotice({ children, variant = 'info' }: { children: React.ReactNode; variant?: 'info' | 'warning' | 'error' }) {
	const Icon = variant === 'info' ? ShieldCheck : AlertTriangle;
	return (
		<div
			role={variant === 'error' ? 'alert' : undefined}
			className={cn(
				'my-3.5 flex items-start gap-3 rounded-lg border px-3.5 py-3 text-[13px] leading-relaxed',
				variant === 'info' && 'border-primary-500/30 bg-primary-500/5 text-primary-200 light:text-primary-800',
				variant === 'warning' && 'border-amber-500/30 bg-amber-500/5 text-amber-200 light:text-amber-800',
				variant === 'error' && 'border-red-500/30 bg-red-500/10 text-red-200 light:text-red-800'
			)}
		>
			<Icon className="mt-0.5 size-[18px] shrink-0" />
			<div>{children}</div>
		</div>
	);
}

function RecoverySummary({ rows, success = false }: { rows: [string, string][]; success?: boolean }) {
	return (
		<dl className={cn('rounded-lg border border-neutral-800 px-3 py-1.5', success && 'border-primary-500/35 bg-primary-500/5')}>
			{rows.map(([label, value]) => (
				<div
					key={label}
					className="flex flex-col gap-1 border-b border-neutral-800/50 py-3 text-[13px] last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
				>
					<dt className="shrink-0 text-neutral-400">{label}</dt>
					<dd className="min-w-0 break-words font-medium text-neutral-100 sm:text-right">{value}</dd>
				</div>
			))}
		</dl>
	);
}

function RecoveryPhoto({
	title,
	hint,
	photo,
	onChange,
	onError
}: {
	title: string;
	hint: string;
	photo: Photo | null;
	onChange: (photo: Photo | null) => void;
	onError: (error: string) => void;
}) {
	const [checking, setChecking] = useState(false);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const select_file = async (file?: File) => {
		if (!file || checking) return;
		onChange(null);
		if (!['image/jpeg', 'image/png'].includes(file.type) || !file.size || file.size > MAX_IMAGE_BYTES) {
			onError('Adjunta una imagen JPEG o PNG de hasta 5 MB.');
			return;
		}
		setChecking(true);
		const url = URL.createObjectURL(file);
		try {
			const image = new Image();
			image.src = url;
			await image.decode();
			if (image.naturalWidth * image.naturalHeight > MAX_IMAGE_PIXELS) {
				throw new Error('La imagen no puede superar los 16 megapíxeles.');
			}
			if (!mounted.current) {
				URL.revokeObjectURL(url);
				return;
			}
			onChange({ file, url });
			onError('');
		} catch (error) {
			URL.revokeObjectURL(url);
			if (mounted.current)
				onError(
					error instanceof Error && error.message.includes('megapíxeles')
						? error.message
						: 'No se puede leer la imagen. Adjunta un JPEG o PNG válido.'
				);
		} finally {
			if (mounted.current) setChecking(false);
		}
	};
	return (
		<div className="min-w-0">
			<p className="mb-2.5 text-sm font-medium text-neutral-100">{title}</p>
			<label
				className={cn(
					'block cursor-pointer rounded-lg focus-within:ring-2 focus-within:ring-primary-500',
					checking && 'pointer-events-none opacity-50'
				)}
			>
				<input
					type="file"
					accept="image/jpeg,image/png"
					aria-label={title}
					disabled={checking}
					className="sr-only"
					onChange={(event) => {
						const file = event.currentTarget.files?.[0];
						event.currentTarget.value = '';
						void select_file(file);
					}}
				/>
				{photo ? (
					<>
						<div className="flex h-36 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-925 p-2">
							<img src={photo.url} alt={title} className="h-full max-w-full rounded object-contain" />
						</div>
						<div className="mt-2 flex items-center gap-2 text-xs">
							<FileImage className="size-4 shrink-0" />
							<span className="min-w-0 flex-1 truncate" title={photo.file.name}>
								{photo.file.name}
							</span>
							<span className="text-primary-400 light:text-primary-700">Cambiar</span>
						</div>
					</>
				) : (
					<div className="flex h-[155px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-600 bg-neutral-925">
						<Upload className="size-6 text-neutral-400" />
						<span className="text-sm font-medium text-neutral-100">{checking ? 'Revisando imagen…' : 'Seleccionar archivo'}</span>
						<span className="text-xs text-neutral-400">JPEG o PNG · Hasta 5 MB</span>
					</div>
				)}
			</label>
			<p className="mt-2 text-[11px] text-neutral-400">
				{photo
					? `${(photo.file.size / 1024 / 1024).toLocaleString('es-VE', { maximumFractionDigits: 1 })} MB · Archivo seleccionado, aún no validado`
					: hint}
			</p>
		</div>
	);
}

export default function IdentityRecovery({
	client,
	userData,
	country,
	disabled = false,
	onClose,
	onAccountRetired
}: {
	client: IdentityRecoveryClient | null;
	userData: UserData | null;
	country: string;
	disabled?: boolean;
	onClose: () => void;
	onAccountRetired: (identification: string, authorizedEmail: string) => void;
}) {
	const allowed = can_recover_identity(userData, country);
	// Isolate drafts by backend, country and operator. Store no images, tokens or old account details.
	const prefix = `identity-recovery:${new URL(toApiUrl(ENDPOINT)).origin}:${country}:${userData?.email}:`;
	const scope = useRef(prefix);
	scope.current = prefix;
	const alive = useRef(true);
	const submitting = useRef(false);
	const retiredCallback = useRef(onAccountRetired);
	retiredCallback.current = onAccountRetired;
	const [requests, setRequests] = useState<SavedRecovery[]>([]);
	const [session, setSession] = useState<RecoverySession | null>(null);
	const [step, setStep] = useState<RecoveryStep>('form');
	const activeRequest = useRef<string | null>(null);
	activeRequest.current = session?.request_id || null;
	const [identityPhoto, setIdentityPhoto] = useState<Photo | null>(null);
	const [selfiePhoto, setSelfiePhoto] = useState<Photo | null>(null);
	const [acknowledged, setAcknowledged] = useState(false);
	const [error, setError] = useState('');
	const [emailError, setEmailError] = useState('');
	const [storageError, setStorageError] = useState('');
	const [copyMessage, setCopyMessage] = useState('');

	useEffect(() => {
		alive.current = true;
		return () => {
			alive.current = false;
		};
	}, []);
	useEffect(
		() => () => {
			if (identityPhoto) URL.revokeObjectURL(identityPhoto.url);
		},
		[identityPhoto]
	);
	useEffect(
		() => () => {
			if (selfiePhoto) URL.revokeObjectURL(selfiePhoto.url);
		},
		[selfiePhoto]
	);
	useEffect(() => {
		setSession(null);
		setIdentityPhoto(null);
		setSelfiePhoto(null);
		setStorageError('');
		const refresh = () => {
			try {
				setRequests(allowed ? read_recoveries(prefix) : []);
			} catch {
				setStorageError(
					'No se pueden leer las solicitudes guardadas en este navegador. Habilita el almacenamiento antes de continuar.'
				);
			}
		};
		refresh();
		window.addEventListener('storage', refresh);
		return () => window.removeEventListener('storage', refresh);
	}, [prefix, allowed]);

	useEffect(() => {
		if (!client || !allowed) return;
		try {
			const previous = read_recoveries(prefix).find(
				(request) => request.expected_identification_number === client.identification_number
			);
			setSession(
				previous
					? { ...previous, current_email: client.email, locked: true }
					: {
							request_id: crypto.randomUUID(),
							expected_identification_number: client.identification_number,
							email: '',
							current_email: client.email,
							state: 'unknown',
							locked: false
						}
			);
			setStep(previous?.state || 'form');
			setIdentityPhoto(null);
			setSelfiePhoto(null);
			setAcknowledged(false);
			setError('');
			setEmailError('');
			setCopyMessage('');
		} catch {
			setStorageError(
				'No se puede conservar la referencia de esta solicitud. Habilita el almacenamiento del navegador antes de continuar.'
			);
		}
	}, [client, allowed, prefix]);

	useEffect(() => {
		if (step !== 'processing') return;
		const prevent_leave = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', prevent_leave);
		return () => window.removeEventListener('beforeunload', prevent_leave);
	}, [step]);

	const refresh_requests = (requestPrefix = prefix) => {
		if (alive.current && scope.current === requestPrefix) setRequests(read_recoveries(requestPrefix));
	};
	const save_request = (request: SavedRecovery, requestPrefix = prefix) => {
		const value: SavedRecovery = {
			request_id: request.request_id,
			email: request.email,
			expected_identification_number: request.expected_identification_number,
			state: request.state
		};
		localStorage.setItem(requestPrefix + request.request_id, JSON.stringify(value));
		refresh_requests(requestPrefix);
	};
	const close = () => {
		if (submitting.current) return;
		if (session && step === 'completed') {
			try {
				localStorage.removeItem(prefix + session.request_id);
				refresh_requests();
			} catch {
				setStorageError('No se pudo borrar la referencia local. La recuperación ya está completada.');
			}
		}
		setSession(null);
		setIdentityPhoto(null);
		setSelfiePhoto(null);
		onClose();
	};
	const resume = (request: SavedRecovery) => {
		setSession({ ...request, current_email: '', locked: true });
		setStep(request.state);
		setError('');
		setEmailError('');
		setCopyMessage('');
		setIdentityPhoto(null);
		setSelfiePhoto(null);
	};
	const copy_text = (text: string) =>
		setCopyMessage(copy(text) ? 'Datos copiados.' : 'No se pudo copiar. Selecciona y copia los datos manualmente.');

	const submit = async () => {
		if (!session || !allowed || submitting.current || (step === 'confirm' && !acknowledged)) return;
		if (!validate_email(session.email) || (step === 'confirm' && (!identityPhoto || !selfiePhoto))) return;
		const requestPrefix = prefix;
		const current = {
			...session,
			email: session.email.trim().toLowerCase(),
			state: 'unknown' as const
		};
		try {
			const other = read_recoveries(prefix).find(
				(request) =>
					request.expected_identification_number === current.expected_identification_number &&
					request.request_id !== current.request_id
			);
			if (other) {
				resume(other);
				return;
			}
			save_request(current);
		} catch {
			setError(
				'No se pudo guardar la referencia. No se envió la solicitud. Habilita el almacenamiento del navegador y reintenta.'
			);
			return;
		}
		submitting.current = true;
		setStep('processing');
		setError('');
		setEmailError('');
		setCopyMessage('');
		const retry: IdentityRecoveryRetry = {
			request_id: current.request_id,
			email: current.email,
			expected_identification_number: current.expected_identification_number
		};
		let payload: FormData | IdentityRecoveryRetry = retry;
		if (identityPhoto && selfiePhoto) {
			const form = new FormData();
			Object.entries(retry).forEach(([key, value]) => form.append(key, value));
			form.append('identification_photo', identityPhoto.file);
			form.append('selfie_photo', selfiePhoto.file);
			payload = form;
		}
		const controller = new AbortController();
		const timeout = window.setTimeout(() => controller.abort(), RESPONSE_TIMEOUT_MS);
		const [response, transportError] = await callAPI(
			ENDPOINT,
			'POST',
			payload,
			userData,
			country,
			true,
			true,
			controller.signal,
			'envelope'
		);
		window.clearTimeout(timeout);
		submitting.current = false;
		const still_here = alive.current && scope.current === requestPrefix && activeRequest.current === current.request_id;
		const boundResult =
			response?.request_id === current.request_id &&
			response.data?.country === country &&
			response.data?.email === current.email &&
			typeof response.data?.identification_number === 'string' &&
			canonical_recovery_identification(response.data.identification_number) !== null &&
			canonical_recovery_identification(response.data.identification_number) ===
				canonical_recovery_identification(current.expected_identification_number);
		const completed = boundResult && response.http_status === 200 && response.success === true && response.processing !== true;
		const pending = boundResult && response.http_status === 202 && response.success === false && response.processing === true;
		if (completed || pending) {
			const state = completed ? 'completed' : 'pending';
			try {
				save_request({ ...current, state }, requestPrefix);
			} catch {
				if (still_here)
					setStorageError('No se pudo actualizar la referencia local. Conserva el identificador para retomar la solicitud.');
			}
			if (!still_here) return;
			setSession({ ...current, state, locked: true });
			setStep(state);
			setIdentityPhoto(null);
			setSelfiePhoto(null);
			retiredCallback.current(current.expected_identification_number, current.email);
			return;
		}
		if (!still_here) return;
		const message =
			transportError || (typeof response?.status === 'string' ? response.status : 'No se pudo confirmar el resultado.');
		// Only these explicit pre-archive rejections permit editing the first attempt.
		// Once a response has been lost, preserve the UUID and email even if a retry fails.
		const editable =
			!current.locked &&
			response?.request_id === current.request_id &&
			(response.http_status === 400 || response.http_status === 409) &&
			[
				...EMAIL_ERROR_CODES,
				'invalid_image',
				'missing_image',
				'invalid_identification',
				'unreadable_identification',
				'selected_identity_mismatch',
				'same_image',
				'ambiguous_face',
				'document_face_mismatch',
				'existing_face_match',
				'duplicate_face',
				'name_mismatch',
				'registry_incomplete',
				'identity_type_mismatch',
				'debt_limit'
			].includes(response?.code || '');
		if (editable) {
			try {
				localStorage.removeItem(requestPrefix + current.request_id);
				refresh_requests();
			} catch {
				setStorageError('No se pudo actualizar la referencia guardada. Conserva el identificador de esta solicitud.');
			}
			setSession(current);
			setStep(response?.code === 'debt_limit' ? 'debt' : 'form');
			if (EMAIL_ERROR_CODES.includes(response?.code || '')) setEmailError(message);
			else setError(message);
		} else {
			setSession({ ...current, locked: true });
			setStep(response?.code === 'missing_image' ? 'form' : 'unknown');
			setError(
				response?.code === 'missing_image'
					? 'Selecciona los mismos archivos que enviaste originalmente para retomar esta solicitud. El correo y la referencia no cambian.'
					: message
			);
		}
	};

	if (!allowed) return null;
	const footer = 'mt-[22px] flex flex-wrap justify-end gap-3 border-t border-neutral-800/60 pt-[18px]';
	const hint = 'mt-4 text-xs leading-relaxed text-neutral-400';
	const registrationRows: [string, string][] = session
		? [
				['Cédula', session.expected_identification_number],
				[step === 'completed' ? 'Correo autorizado' : 'Correo reservado', session.email]
			]
		: [];
	return (
		<>
			{storageError && <RecoveryNotice variant="error">{storageError}</RecoveryNotice>}
			{requests.length > 0 && (
				<div className="mt-5 rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
					<h3 className="text-sm font-medium text-neutral-100">Solicitudes de recuperación guardadas</h3>
					<p className="mt-1 text-xs text-neutral-400">
						Puedes retomarlas desde este navegador, aunque la cuenta anterior ya no aparezca en el buscador.
					</p>
					<div className="mt-3 max-h-60 space-y-2 overflow-y-auto">
						{requests.map((request) => (
							<div
								key={request.request_id}
								className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800/50 pt-2 text-sm"
							>
								<div>
									<span className="font-medium">{request.expected_identification_number}</span>
									<span className="ml-3 text-xs text-neutral-400">
										{request.state === 'completed'
											? 'Completada'
											: request.state === 'pending'
												? 'Finalización pendiente'
												: 'Resultado por confirmar'}
									</span>
								</div>
								<Button className="!w-auto" variant="secondary" disabled={disabled} onClick={() => resume(request)}>
									Ver solicitud
								</Button>
							</div>
						))}
					</div>
				</div>
			)}
			{session && (
				<Dialog
					title={
						<span className="flex items-center gap-2.5">
							<ShieldCheck className="size-5" />
							Recuperar identidad
						</span>
					}
					className="!w-[790px]"
					onClose={close}
					closable={step !== 'processing'}
					cerrarButton={false}
					disableOverlayClose
				>
					<div
						aria-live="polite"
						onKeyDown={(event) => {
							if (event.key === 'Enter') event.stopPropagation();
						}}
					>
						{step === 'form' && (
							<>
								<div className="mb-4 flex flex-wrap items-center gap-3 text-[13px] text-neutral-400">
									<span>Cuenta reportada</span>
									<b className="font-medium text-neutral-100">{session.expected_identification_number}</b>
									<span className="min-w-0 break-all sm:ml-auto">{session.current_email}</span>
								</div>
								<RecoveryNotice>
									{session.locked
										? 'Retoma la solicitud original. No cambies los archivos ni crees una recuperación nueva.'
										: 'Carga las fotos del titular legítimo y el correo que usará para registrarse. La cuenta no cambia hasta que confirmes.'}
								</RecoveryNotice>
								{error && (
									<RecoveryNotice variant="error">
										{!session.locked && (
											<strong className="mb-1 block text-sm font-semibold">No se pudo validar la identidad</strong>
										)}
										{error}
									</RecoveryNotice>
								)}
								<div className="mt-5 grid gap-4 sm:grid-cols-2">
									<RecoveryPhoto
										title="Foto de la cédula"
										hint="Documento completo y legible"
										photo={identityPhoto}
										onChange={setIdentityPhoto}
										onError={setError}
									/>
									<RecoveryPhoto
										title="Selfie del titular"
										hint="Una sola cara, sin filtros"
										photo={selfiePhoto}
										onChange={setSelfiePhoto}
										onError={setError}
									/>
								</div>
								<div className="mt-5">
									<label htmlFor="identity-recovery-email" className="mb-2 block text-sm font-medium text-neutral-100">
										Correo nuevo del titular
									</label>
									<Input
										id="identity-recovery-email"
										name="recovery_email"
										placeholder="Ingresa el correo electrónico"
										type="email"
										value={session.email}
										readonly={session.locked}
										clearable={!session.locked}
										Icon={<Mail className="size-4 text-neutral-400" />}
										onChange={(email) => {
											setSession({ ...session, email });
											setEmailError('');
										}}
									/>
									<p className="mt-2 text-xs text-neutral-400">
										Debe estar libre. Será el único correo autorizado para registrar esta cédula.
									</p>
									{emailError && <RecoveryNotice variant="error">{emailError}</RecoveryNotice>}
								</div>
								<p className={hint}>
									Se compararán los rostros, el nombre en el registro oficial y el saldo total pendiente. El límite es USD 60,
									incluidas las cuotas futuras.
								</p>
								<div className={footer}>
									<Button className="!w-auto" variant="secondary" onClick={close}>
										Cancelar
									</Button>
									<Button
										className="!w-auto"
										variant="primary"
										disabled={!identityPhoto || !selfiePhoto || !validate_email(session.email) || Boolean(emailError)}
										onClick={() => {
											setSession({
												...session,
												email: session.email.trim().toLowerCase()
											});
											setAcknowledged(false);
											setError('');
											setStep('confirm');
										}}
									>
										Revisar solicitud
									</Button>
								</div>
							</>
						)}
						{step === 'confirm' && (
							<>
								<p className="mb-4 text-base font-medium text-neutral-100">Revisa los datos antes de continuar</p>
								<RecoverySummary
									rows={[
										['Cédula de la cuenta reportada', session.expected_identification_number],
										...(session.current_email ? [['Correo actual', session.current_email] as [string, string]] : []),
										['Correo autorizado para el nuevo registro', session.email]
									]}
								/>
								<RecoveryNotice variant="warning">
									<strong className="mb-1 block text-sm font-semibold">Esta acción cambia la cuenta</strong>
									Si todas las validaciones son satisfactorias, esta operación retirará la cuenta actual.
								</RecoveryNotice>
								<ul className="my-4 list-disc space-y-2 pl-5 text-sm leading-relaxed">
									<li>Se guardarán los datos y la evidencia en un expediente de fraude.</li>
									<li>Se retirará la cuenta y se bloquearán su correo y teléfono.</li>
									<li>La cédula quedará reservada para el correo nuevo.</li>
								</ul>
								<p className={hint}>
									Los movimientos y contratos históricos se conservan. Esto no registra automáticamente al titular.
								</p>
								<label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-neutral-800 bg-neutral-950/30 p-3 text-[13px] leading-relaxed">
									<input
										type="checkbox"
										className="mt-1 size-4 shrink-0 accent-red-500"
										checked={acknowledged}
										onChange={(event) => setAcknowledged(event.target.checked)}
									/>
									<span>Confirmo que revisé los archivos y que entiendo los cambios sobre esta cuenta.</span>
								</label>
								{error && <RecoveryNotice variant="error">{error}</RecoveryNotice>}
								<div className={footer}>
									<Button className="!w-auto" variant="secondary" onClick={() => setStep('form')}>
										Volver
									</Button>
									<Button className="!w-auto" variant="destructive" disabled={!acknowledged} onClick={submit}>
										Confirmar recuperación
									</Button>
								</div>
							</>
						)}
						{step === 'processing' && (
							<>
								<div role="status" className="pb-5 pt-4 text-center">
									<div className="mb-6 flex justify-center">
										<Spinner size={2.4} />
									</div>
									<h2 className="mb-2 text-[22px] font-semibold text-neutral-100">Procesando recuperación</h2>
									<p className="text-sm text-neutral-400">Estamos validando la identidad y la cuenta.</p>
								</div>
								<RecoverySummary rows={registrationRows} />
								<p className="my-6 text-sm leading-relaxed text-neutral-400">
									Si todo es válido, se archivará la evidencia y se retirará la cuenta actual. No envíes una segunda solicitud.
								</p>
								<RecoveryNotice>Espera la respuesta para saber si terminó o si necesita un reintento.</RecoveryNotice>
								<div className={footer}>
									<Button className="!w-auto" variant="secondary" disabled>
										Procesando…
									</Button>
								</div>
							</>
						)}
						{['completed', 'pending', 'unknown', 'debt'].includes(step) && (
							<>
								<div className="pb-5 pt-2 text-center">
									<div
										className={cn(
											'mx-auto mb-4 flex size-[62px] items-center justify-center rounded-full',
											step === 'completed'
												? 'bg-green-400/10 text-green-400 light:text-green-700'
												: step === 'debt'
													? 'bg-red-400/10 text-red-400 light:text-red-700'
													: 'bg-amber-400/10 text-amber-400 light:text-amber-700'
										)}
									>
										{step === 'completed' ? (
											<Check size={30} />
										) : step === 'debt' ? (
											<AlertTriangle size={30} />
										) : (
											<Clock size={30} />
										)}
									</div>
									<h2 className="mb-2 text-[22px] font-semibold text-neutral-100">
										{step === 'completed'
											? 'Identidad recuperada'
											: step === 'debt'
												? 'No se puede recuperar esta cuenta'
												: step === 'pending'
													? 'La recuperación sigue pendiente'
													: 'No pudimos confirmar el resultado'}
									</h2>
									<p className="text-sm leading-relaxed text-neutral-400">
										{step === 'completed' ? (
											<>
												El titular ya puede iniciar un nuevo registro
												<br />
												desde la app o desde una tienda.
											</>
										) : step === 'pending' ? (
											<>
												El expediente se guardó y la cuenta anterior se retiró.
												<br />
												Falta terminar la desvinculación de identidad.
											</>
										) : step === 'debt' ? (
											'El saldo pendiente total supera USD 60.'
										) : (
											'No sabemos si la operación terminó. Conserva esta solicitud y reintenta con la misma referencia.'
										)}
									</p>
								</div>
								{step === 'pending' && (
									<RecoveryNotice variant="warning">
										<strong className="mb-1 block text-sm font-semibold">No iniciar el nuevo registro</strong>
										El titular todavía no puede registrarse, ni siquiera con el correo autorizado.
									</RecoveryNotice>
								)}
								<RecoverySummary
									success={step === 'completed'}
									rows={
										step === 'debt'
											? [
													['Cédula reportada', session.expected_identification_number],
													['Estado de la cuenta', 'Sin cambios']
												]
											: registrationRows
									}
								/>
								{step === 'completed' && (
									<>
										<div className="pt-4 text-sm leading-8">
											{['Expediente guardado', 'Cuenta anterior retirada', 'Correo y teléfono anteriores bloqueados'].map(
												(text) => (
													<div key={text} className="flex items-center gap-2.5">
														<Check className="size-4 text-green-400 light:text-green-700" />
														{text}
													</div>
												)
											)}
										</div>
										<p className={hint}>El titular debe completar el registro habitual, incluida la verificación de identidad.</p>
									</>
								)}
								{step === 'debt' && (
									<>
										<RecoveryNotice variant="error">
											El límite incluye las cuotas vencidas y las cuotas futuras. No se archivó ni se retiró la cuenta.
										</RecoveryNotice>
										<p className={hint}>
											Revisa la deuda desde la ficha del cliente. Este flujo no permite omitir el límite ni forzar la
											recuperación.
										</p>
									</>
								)}
								{(step === 'pending' || step === 'unknown') && (
									<>
										<div className="mt-4">
											<p className="mb-2 text-xs text-neutral-400">Identificador de la solicitud</p>
											<div className="flex items-center justify-between gap-2 rounded-lg bg-neutral-950 p-3">
												<code className="min-w-0 break-all text-[13px]">{session.request_id}</code>
												<Button
													variant="text"
													className="!w-auto shrink-0 p-1"
													aria-label="Copiar identificador de la solicitud"
													onClick={() => copy_text(session.request_id)}
												>
													<Copy className="size-4" />
												</Button>
											</div>
										</div>
										<p className={hint}>
											Reintenta esta misma solicitud. Si continúa pendiente, envíala a revisión interna; no crees una recuperación
											nueva.
										</p>
										{error && <RecoveryNotice variant="error">{error}</RecoveryNotice>}
									</>
								)}
								{copyMessage && (
									<p role="status" className="mt-3 text-xs text-neutral-300">
										{copyMessage}
									</p>
								)}
								<div className={footer}>
									{step === 'completed' && (
										<Button
											className="!w-auto"
											variant="secondary"
											Icon={<Copy className="size-4" />}
											onClick={() =>
												copy_text(`Cédula: ${session.expected_identification_number}\nCorreo autorizado: ${session.email}`)
											}
										>
											Copiar datos de registro
										</Button>
									)}
									<Button className="!w-auto" variant={step === 'completed' ? 'primary' : 'secondary'} onClick={close}>
										{step === 'debt' ? 'Volver a la ficha' : 'Cerrar'}
									</Button>
									{(step === 'pending' || step === 'unknown') && (
										<Button className="!w-auto" variant="primary" onClick={submit}>
											{step === 'pending' ? 'Reintentar finalización' : 'Reintentar misma solicitud'}
										</Button>
									)}
								</div>
							</>
						)}
					</div>
				</Dialog>
			)}
		</>
	);
}
